import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Space, Switch, Typography, message } from 'antd';
import { ReloadOutlined, SaveOutlined, SettingOutlined } from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import { useModulos } from '../../../context/modulosContext';
import MainLayout from '../../layout/layout';

const { Text } = Typography;

const ConfiguracionGestion = () => {
  const { reloadModulos } = useModulos();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clubSenia, setClubSenia] = useState(true);
  const [academiaPagosMuevenCaja, setAcademiaPagosMuevenCaja] = useState(false);
  const [academiaMercadoPagoActivo, setAcademiaMercadoPagoActivo] = useState(false);
  const [recalculoTurnosDespuesClase, setRecalculoTurnosDespuesClase] = useState(false);
  const [clubTurnosDuracionVariableAdmin, setClubTurnosDuracionVariableAdmin] = useState(false);
  const [clubTurnosCobrosMuevenCaja, setClubTurnosCobrosMuevenCaja] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/modulos/config');
      if (response.ok) {
        const data = await response.json();
        setClubSenia(data?.clubSenia !== false);
        setAcademiaPagosMuevenCaja(Boolean(data.academiaPagosMuevenCaja));
        setAcademiaMercadoPagoActivo(Boolean(data.academiaMercadoPagoActivo));
        setRecalculoTurnosDespuesClase(Boolean(data.recalculoTurnosDespuesClase));
        setClubTurnosDuracionVariableAdmin(Boolean(data.clubTurnosDuracionVariableAdmin));
        setClubTurnosCobrosMuevenCaja(data.clubTurnosCobrosMuevenCaja !== false);
      } else {
        message.error('Error al cargar la configuración de gestión');
      }
    } catch (error) {
      console.error('Error al cargar configuración de gestión:', error);
      message.error('Error al cargar la configuración de gestión');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiClient.put('/modulos/gestion/config', {
        clubSenia,
        academiaPagosMuevenCaja,
        academiaMercadoPagoActivo,
        recalculoTurnosDespuesClase,
        clubTurnosDuracionVariableAdmin,
        clubTurnosCobrosMuevenCaja,
      });

      if (response.ok) {
        message.success('Configuración actualizada correctamente');
        await reloadModulos?.();
      } else {
        const error = await response.json().catch(() => ({}));
        message.error(error.message || 'Error al actualizar la configuración');
      }
    } catch (error) {
      console.error('Error al guardar configuración de gestión:', error);
      message.error('Error al actualizar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const renderConfigCard = ({
    accentColor,
    title,
    description,
    active,
    onChange,
    activeMessage,
    inactiveMessage,
  }) => (
    <Card
      style={{
        background: 'linear-gradient(180deg, #303030 0%, #262626 100%)',
        border: `1px solid ${active ? 'rgba(216,255,62,0.35)' : '#434343'}`,
        borderRadius: 16,
        boxShadow: active
          ? '0 0 0 1px rgba(216,255,62,0.08), 0 14px 34px rgba(0,0,0,0.24)'
          : '0 10px 26px rgba(0,0,0,0.22)',
      }}
      styles={{ body: { padding: isMobile ? 16 : 22 } }}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              display: 'grid',
              placeItems: 'center',
              background: `${accentColor}22`,
              border: `1px solid ${accentColor}55`,
              flexShrink: 0,
            }}
          >
            <SettingOutlined style={{ color: accentColor, fontSize: 22 }} />
          </div>
          <div style={{ flex: 1 }}>
            <Text strong style={{ color: '#ffffff', display: 'block', fontSize: 17 }}>
              {title}
            </Text>
            <Text style={{ color: '#b0b0b0', lineHeight: 1.5 }}>
              {description}
            </Text>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
            padding: '14px 16px',
            backgroundColor: '#171717',
            borderRadius: 12,
            border: '1px solid #3d3d3d',
          }}
        >
          <div>
            <Text strong style={{ color: '#ffffff', display: 'block' }}>
              {active ? 'Activado' : 'Desactivado'}
            </Text>
            <Text style={{ color: '#8c8c8c', fontSize: 12 }}>
              {active ? 'La opción está habilitada' : 'La opción está pausada'}
            </Text>
          </div>
          <Switch
            checked={active}
            onChange={onChange}
            checkedChildren="Sí"
            unCheckedChildren="No"
          />
        </div>

        <Alert
          type={active ? 'success' : 'info'}
          showIcon
          message={active ? activeMessage : inactiveMessage}
          style={{
            borderRadius: 12,
            background: active ? 'rgba(22, 74, 32, 0.38)' : 'rgba(17, 47, 92, 0.38)',
          }}
        />
      </Space>
    </Card>
  );

  return (
    <MainLayout>
      <div
        style={{
          minHeight: '100%',
          padding: isMobile ? 16 : 32,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <div style={{ width: '100%', maxWidth: 860 }}>
          <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? 22 : 28 }}>
            Configuración de Gestión
          </h1>
          <Space wrap style={{ justifyContent: 'center' }}>
            <Button icon={<ReloadOutlined />} onClick={loadConfig} loading={loading}>
              {isMobile ? null : 'Actualizar'}
            </Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
              Guardar
            </Button>
          </Space>
        </div>

        <Space direction="vertical" size={18} style={{ width: '100%' }}>
          {renderConfigCard({
            accentColor: '#d8ff3e',
            title: 'Cobrar seña en turnos con Mercado Pago',
            description: 'Activa el cobro online de la seña al reservar canchas desde jugador, alumno o reserva pública.',
            active: clubSenia,
            onChange: setClubSenia,
            activeMessage: 'Las reservas de cancha intentarán cobrar seña online según la configuración de Club.',
            inactiveMessage: 'Las reservas de cancha no pedirán seña online con Mercado Pago.',
          })}

          {renderConfigCard({
            accentColor: '#1890ff',
            title: 'Pagos de alumnos mueven caja',
            description: 'Activa el cobro de mensualidades de Academia desde Gestión y registra esos ingresos en Caja Diaria.',
            active: academiaPagosMuevenCaja,
            onChange: setAcademiaPagosMuevenCaja,
            activeMessage: 'La pestaña Academia se verá en Cobros y los pagos registrados moverán Caja Diaria.',
            inactiveMessage: 'Academia no se verá en Cobros y los pagos de alumnos no crearán movimientos de caja.',
          })}

          {renderConfigCard({
            accentColor: '#d8ff3e',
            title: 'Cobrar mensualidades con Mercado Pago',
            description: 'Activa el checkout online para que el alumno pague el valor del plan desde Mis compras.',
            active: academiaMercadoPagoActivo,
            onChange: setAcademiaMercadoPagoActivo,
            activeMessage: 'El alumno será redirigido a Mercado Pago y el pago se marcará como cobrado recién al recibir el webhook.',
            inactiveMessage: 'El alumno no podrá iniciar pagos online desde Mis compras.',
          })}

          {renderConfigCard({
            accentColor: '#faad14',
            title: 'Recalculo de turnos despues de clase',
            description: 'Permite que los turnos disponibles posteriores a una clase comiencen exactamente cuando termina la clase.',
            active: recalculoTurnosDespuesClase,
            onChange: setRecalculoTurnosDespuesClase,
            activeMessage: 'Después de una clase, el próximo turno podrá arrancar en el horario real de finalización de esa clase.',
            inactiveMessage: 'Los turnos posteriores a clases seguirán respetando el alineamiento normal de la grilla.',
          })}

          {renderConfigCard({
            accentColor: '#13c2c2',
            title: 'Cobros de turnos mueven caja',
            description: 'Si está activo, también el artículo turno/alquiler entra en Caja Diaria. Si está desactivado, solo ese artículo queda fuera; consumos y otras ventas del turno sí mueven caja.',
            active: clubTurnosCobrosMuevenCaja,
            onChange: setClubTurnosCobrosMuevenCaja,
            activeMessage: 'Todos los cobros del turno (incluido el artículo turno) se suman en Caja Diaria y entran en la rendición.',
            inactiveMessage: 'Solo el artículo turno/alquiler no afecta Caja Diaria ni la rendición. Bebidas, pelotas y demás ventas del turno sí entran en caja.',
          })}

          {renderConfigCard({
            accentColor: '#722ed1',
            title: 'Duraciones variables en agenda Club',
            description: 'Permite que el administrador reserve canchas con duraciones de 1h, 1:30h y 2h, con precio propio por duración.',
            active: clubTurnosDuracionVariableAdmin,
            onChange: setClubTurnosDuracionVariableAdmin,
            activeMessage: 'La agenda admin permitirá elegir duración y usará precios configurados por duración.',
            inactiveMessage: 'La agenda admin seguirá usando la duración del deporte como hasta ahora.',
          })}
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

export default ConfiguracionGestion;
